import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { Recipe } from '../../types/recipe';
import WeeklyRecipeCard from '../recipe/WeeklyRecipeCard';
import { getWeeklyRecipes } from '../../utils/weeklyRecipes';

type Props = {
  recipes: Recipe[];
  onRecipeClick: (recipe: Recipe) => void;
};

const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export default function WeeklyRecipes({ recipes, onRecipeClick }: Props) {
  // Get current day index (0 = Sunday, 1 = Monday, etc.)
  const getCurrentDayIndex = () => {
    return new Date().getDay();
  };
  
  const [currentIndex, setCurrentIndex] = useState(getCurrentDayIndex());
  const weeklyRecipes = getWeeklyRecipes(recipes);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 2 >= DAYS.length ? 0 : prev + 2));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 2 < 0 ? DAYS.length - 2 : prev - 2));
  };

  // Configuration pour les gestes tactiles
  const handlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false }
  });

  return (
    <section className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Recettes de la semaine</h2>
        <div className="flex gap-2">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div 
        {...handlers}
        className="relative overflow-hidden rounded-lg"
      >
        <div 
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${(currentIndex / 2) * 100}%)` }}
        >
          {Array.from({ length: Math.ceil(DAYS.length / 2) }).map((_, slideIndex) => (
            <div key={slideIndex} className="w-full flex-shrink-0">
              <div className="grid grid-cols-2 gap-3">
                {[0, 1].map((offset) => {
                  const dayIndex = (slideIndex * 2 + offset) % DAYS.length;
                  const recipe = weeklyRecipes[dayIndex];
                  return (
                    <WeeklyRecipeCard
                      key={dayIndex}
                      recipe={recipe}
                      day={DAYS[dayIndex]}
                      onRecipeClick={onRecipeClick}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicateurs de pagination */}
      <div className="flex justify-center mt-4 space-x-2">
        {Array.from({ length: Math.ceil(DAYS.length / 2) }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index * 2)}
            className={`w-2 h-2 rounded-full transition-colors ${
              Math.floor(currentIndex / 2) === index 
                ? 'bg-orange-500' 
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Indication de swipe pour les utilisateurs */}
      <p className="text-center text-xs text-gray-400 mt-2">
        Glissez pour naviguer
      </p>
    </section>
  );
}